import React, { useState } from 'react';
import { createRoot } from 'react-dom/client';
import PhoneInput, { getCountryCallingCode, isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input';
import ReCAPTCHA from 'react-google-recaptcha';
import 'react-phone-number-input/style.css';
import './classes/TalkWithLynnForm.css';
import { configData } from '../config';
import { isBlockedEmail } from './services/QuickAppService';

// The Talk With Dr. Lashbrook form (node 606), which the page's body holds as the HTML of Drupal's
// lynn_form. On the live site the page's own script filled in the country and state lists, put an
// intl-tel-input phone field and a reCAPTCHA in it and checked the fields; the form then posted to
// the Drupal site, which checked the reCAPTCHA, passed the fields on to talkwithlynn/application and
// sent the visitor to /question-received. Scripts in a body are not run here (Body.js), so this does
// the same with the form as the page stores it.

// Drupal's state lists: the US (with its territories) or Canada, by name
const CANADIAN_PROVINCES = ['AB', 'BC', 'MB', 'NB', 'NL', 'NT', 'NS', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT'];
const STATE_COUNTRIES = ['US', 'CA'];
const SENT_PATH = '/question-received';

let listsPromise = null;
const loadLists = () => {
    if (!listsPromise) {
        const get = (path) => fetch(configData.APP_URL + path)
            .then((response) => response.json())
            .then((data) => Object.values(data?.data?.result || {}));
        listsPromise = Promise.all([get('smwwcountries/getcountrieslist'), get('smwwstates/getstatelist')])
            .then(([countries, states]) => ({
                countries,
                states: states.filter((state) => state.abbreviation).sort((a, b) => a.name.localeCompare(b.name)),
            }))
            .catch((err) => {
                listsPromise = null;
                console.error(err);
                return { countries: [], states: [] };
            });
    }
    return listsPromise;
};

// The page script's email check (the same function in other body forms' scripts, e.g. the AC-form's)
export const isEmail = (s) => {
    if (s.includes("'") || s.includes(' ')) return false;
    const at = s.indexOf('@', 1);
    if (at < 1) return false;
    const dot = s.indexOf('.', at + 2);
    return dot !== -1 && dot < s.length - 1;
};

// The country's flag (a list to pick another) and dialling code in front of the number, as
// intl-tel-input showed them on the live site (separateDialCode)
const PhoneField = ({ onChange }) => {
    const [value, setValue] = useState();
    const [country, setCountry] = useState('US');
    return (
        <div className="talk-with-lynn-phone">
            <PhoneInput
                defaultCountry="US"
                addInternationalOption={false}
                placeholder="201-555-0123"
                value={value}
                onCountryChange={setCountry}
                onChange={(phone) => {
                    setValue(phone);
                    onChange(phone || '');
                }}
                numberInputProps={{ id: 'edit-app-cell-phone', className: 'form-text rounded', maxLength: 128 }}
            />
            {country && <span className="talk-with-lynn-phone__dial-code">+{getCountryCallingCode(country)}</span>}
        </div>
    );
};

// Mounts a React widget in place of one of the form's elements; the returned function puts the
// element back (the body keeps the form when its effects run again)
export const mountInPlaceOf = (element, widget) => {
    const holder = document.createElement('div');
    element.replaceWith(holder);
    const root = createRoot(holder);
    root.render(widget);
    return () => {
        holder.replaceWith(element);
        // Not while React is still working on the page that calls this
        setTimeout(() => root.unmount());
    };
};

// Sets up a lynn_form found in a page body; returns the function that undoes it
export const setUpTalkWithLynnForm = (form) => {
    const find = (selector) => form.querySelector(selector);
    const country = find('#fcountry');
    const state = find('#fstate');
    const stateItem = find('#ffstate');
    const provinceItem = find('#fprovince');
    const submit = find('#edit-submit');
    const undo = [];
    let alive = true;
    let lists = { countries: [], states: [] };
    let phone = '';
    let captcha = null;
    const recaptchaRef = React.createRef();

    // The page script's own handlers (changeCountry, form_validation) are not on this site
    country?.removeAttribute('onchange');
    submit?.removeAttribute('onclick');
    form.classList.add('talk-with-lynn-form');

    // The page script's changeCountry: a state list for the US and Canada, a province box elsewhere
    const showCountry = () => {
        if (!country || !state) return;
        const withState = STATE_COUNTRIES.includes(country.value);
        if (stateItem) stateItem.style.display = withState ? '' : 'none';
        if (provinceItem) provinceItem.style.display = withState ? 'none' : 'block';
        if (withState) {
            const province = find('#province');
            if (province) province.value = '';
            const states = lists.states.filter((item) => CANADIAN_PROVINCES.includes(item.abbreviation) === (country.value === 'CA'));
            state.replaceChildren(new Option('-Select State/Province-', ''), ...states.map((item) => new Option(item.name, item.abbreviation)));
        } else {
            state.value = '';
        }
    };
    loadLists().then((loaded) => {
        if (!alive || !country) return;
        lists = loaded;
        country.replaceChildren(...lists.countries.map((item) => new Option(item.label, item.value)));
        country.value = 'US';
        showCountry();
    });
    country?.addEventListener('change', showCountry);

    const phoneInput = find('#edit-app-cell-phone');
    if (phoneInput) {
        undo.push(mountInPlaceOf(phoneInput, <PhoneField onChange={(value) => { phone = value; }} />));
    }
    const captchaBox = find('.g-recaptcha');
    if (captchaBox) {
        const placeholder = document.createElement('div');
        captchaBox.replaceChildren(placeholder);
        undo.push(mountInPlaceOf(placeholder, (
            <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={configData.GOOGLE_RECAPTCHA_SITE_KEY}
                onChange={(token) => { captcha = token; }}
                onExpired={() => { captcha = null; }}
            />
        )));
    }

    // The page script's form_validation: the first problem, in its order and words
    const problem = () => {
        const value = (name) => form.elements[name]?.value || '';
        const email = value('app_email');
        const email2 = value('app_email2');
        if (value('first_name') === '') return ['#edit-first-name', 'Please fill in first name.'];
        if (value('last_name') === '') return ['#edit-last-name', 'Please fill in last name.'];
        if (email === '') return ['#edit-app-email', 'Please fill in email address.'];
        if (!isEmail(email)) return ['#edit-app-email', 'Please fill in valid email address.'];
        if (isBlockedEmail(email)) {
            return ['#edit-app-email', email.toLowerCase() === 'info@smww.com'
                ? 'Please enter your email address (cannot be info@smww.com)'
                : 'Please enter your email address.'];
        }
        if (email2 === '') return ['#edit-app-email2', 'Please fill in email address.'];
        if (!isEmail(email2)) return ['#edit-app-email2', 'Please fill in valid email address.'];
        if (email !== email2) return [null, 'Email not matched.'];
        if (!phone || !isValidPhoneNumber(phone)) return ['#edit-app-cell-phone', 'Please fill in valid primary phone number.'];
        if (value('app_country') === '') return ['#fcountry', 'Please Select Country'];
        if (STATE_COUNTRIES.includes(value('app_country')) && value('app_state') === '') return ['#fstate', 'Please Select State'];
        if (value('app_elaborate') === '') return ['#edit-app-elaborate', 'Please fill in Your Comments or Questions'];
        if (!captcha) return [null, "Please verify you're not a robot."];
        return null;
    };

    const onSubmit = async (event) => {
        event.preventDefault();
        if (submit?.disabled) return;
        const found = problem();
        if (found) {
            const [selector, message] = found;
            if (selector) find(selector)?.classList.add('error');
            alert(message);
            return;
        }
        const label = submit?.value;
        if (submit) {
            submit.value = 'Please wait...';
            submit.disabled = true;
        }
        // What the form posted on the live site: its own fields, the number as typed (without the
        // dialling code, which intl-tel-input showed apart) and the reCAPTCHA's answer
        const fields = new URLSearchParams(new FormData(form));
        fields.set('app_cell_phone', parsePhoneNumber(phone).formatNational());
        fields.set('full_phone', phone);
        fields.set('page_url', window.location.href);
        fields.set('g-recaptcha-response', captcha);
        try {
            const response = await fetch(configData.APP_URL + 'talkwithlynn/application', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: fields.toString(),
            });
            const data = await response.json();
            if (data?.data?.result === 'user_created') {
                window.location.assign(SENT_PATH);
                return;
            }
        } catch (err) {
            console.error(err);
        }
        // A reCAPTCHA answer counts once
        captcha = null;
        recaptchaRef.current?.reset();
        if (submit) {
            submit.value = label;
            submit.disabled = false;
        }
        alert('Your question could not be sent. Please try again.');
    };
    form.addEventListener('submit', onSubmit);

    return () => {
        alive = false;
        form.classList.remove('talk-with-lynn-form');
        country?.removeEventListener('change', showCountry);
        form.removeEventListener('submit', onSubmit);
        undo.forEach((fn) => fn());
    };
};
